
import { supabase } from '@/integrations/supabase/client';
import { saveToSessionStorage, removeFromSessionStorage } from '@/utils/storage/sessionStorageService';

// Check if current session is an admin session
export const isAdminSession = (): boolean => {
  return localStorage.getItem('adminSession') === 'true';
};

// Quick check if user has admin rights without a full API call
export const checkAdminRights = async (): Promise<boolean> => {
  try {
    console.log('Checking admin rights...');
    // Get current user
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      return false;
    }
    
    if (!sessionData.session) {
      console.log('No active session found');
      return false;
    }
    
    console.log('Active session found, checking admin status for user:', sessionData.session.user.id);
    
    // Check if user is in admin_users table
    const { data: adminData, error: adminError } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', sessionData.session.user.id)
      .maybeSingle();
    
    if (adminError) {
      console.error('Admin check error:', adminError);
      return false;
    }
    
    if (!adminData) {
      console.log('No admin record found');
      return false;
    }
    
    console.log('Admin record found:', adminData);
    return true;
  } catch (error) {
    console.error('Error checking admin rights:', error);
    return false;
  }
};

// Set up admin session
export const setupAdminSession = () => {
  console.log('Setting up admin session');
  localStorage.setItem('adminSession', 'true');
};

// Clear admin session 
export const clearAdminSession = () => {
  console.log('Clearing admin session data');
  
  // Remove all admin-related items from localStorage
  localStorage.removeItem('adminSession');
  localStorage.removeItem('lastAdminActivity');
  
  // Also clear from sessionStorage if it might be there
  try {
    removeFromSessionStorage('adminSession');
    removeFromSessionStorage('lastAdminActivity');
  } catch (e) {
    console.error('Error clearing admin session from sessionStorage:', e);
  }
  
  console.log('Admin session data cleared successfully');
};

// Verify if a user exists in the system (either in profiles or admin_users)
export const verifyUserExists = async (email: string): Promise<boolean> => {
  try {
    console.log('Verifying if user exists:', email);
    
    // Fix: The auth.admin.listUsers is giving type errors because it's not properly 
    // available in the client SDK. Instead, we'll check directly in the tables.
    
    // Check profiles table first
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('email')
      .eq('email', email)
      .maybeSingle();
    
    if (profileError) {
      console.error('Error checking profiles table:', profileError);
    }
    
    // If found in profiles, return true
    if (profileData) {
      console.log('User found in profiles table');
      return true;
    }
    
    // If not found in profiles, check admin_users table
    const { data: adminData, error: adminError } = await supabase
      .from('admin_users')
      .select('email')
      .eq('email', email)
      .maybeSingle();
    
    if (adminError) {
      console.error('Error checking admin_users table:', adminError);
      return false;
    }
    
    const userExists = !!adminData;
    console.log('User found in admin_users table:', userExists);
    
    return userExists;
  } catch (error) {
    console.error('Error verifying user exists:', error);
    return false;
  }
};

// Create admin user through Supabase Auth API
export const createAdminUser = async (
  email: string, 
  password: string, 
  name: string = 'Admin User'
): Promise<{ success: boolean, message: string, userId?: string }> => {
  try {
    console.log('Creating admin user:', email);
    
    // Step 1: Create user in auth system
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          is_admin: true,
        },
      }
    });
    
    if (error) {
      console.error('Error creating admin user:', error);
      return { 
        success: false, 
        message: `Failed to create user: ${error.message}` 
      };
    }
    
    if (!data.user) {
      return { 
        success: false, 
        message: 'User creation failed for unknown reason' 
      };
    }
    
    const userId = data.user.id;
    console.log('User created successfully with ID:', userId);
    
    // Step 2: Insert directly into admin_users table with user_id
    const { error: adminError } = await supabase
      .from('admin_users')
      .insert({
        user_id: userId,
        email: email,
        name: name,
        role: 'admin'
      });
    
    if (adminError) {
      console.error('Error creating admin_users record:', adminError);
      return { 
        success: false, 
        message: `User created but admin record creation failed: ${adminError.message}`,
        userId
      };
    }
    
    console.log('Admin user created and linked successfully');
    return { 
      success: true, 
      message: 'Admin user created successfully',
      userId
    };
  } catch (error: any) {
    console.error('Exception in admin user creation:', error);
    return { 
      success: false, 
      message: `Exception occurred: ${error.message || 'Unknown error'}` 
    };
  }
};
